import { createContext, useContext, useState, ReactNode } from "react";
import kaTranslations from "@/translations/ka";
import enTranslations from "@/translations/en";

type Language = "en" | "ka";

const TRANSLATIONS = {
  ka: kaTranslations,
  en: enTranslations,
} as const;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  tRaw: (key: string) => any;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

// Dedupe missing-key warnings (dev-only) so HMR / repeated renders don't spam the console.
const warnedMissingKeys = new Set<string>();
const warnMissingKey = (key: string) => {
  if (!import.meta.env.DEV) return;
  if (warnedMissingKeys.has(key)) return;
  warnedMissingKeys.add(key);
  console.warn(`Translation key not found: ${key}`);
};

export function LanguageProvider({ children }: LanguageProviderProps) {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem("preferred-language");
    return (saved === "ka" || saved === "en") ? saved : "ka";
  });

  const translations = TRANSLATIONS[language];

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem("preferred-language", lang);
  };

  const t = (key: string): string => {
    if (!translations || typeof translations !== 'object') {
      return key;
    }
    
    const keys = key.split(".");
    let value: any = translations;
    
    for (const k of keys) {
      if (value && typeof value === "object" && k in value) {
        value = value[k];
      } else {
        warnMissingKey(key);
        return key;
      }
    }
    
    return typeof value === "string" ? value : key;
  };

  const tRaw = (key: string): any => {
    if (!translations || typeof translations !== 'object') {
      return key;
    }
    
    const keys = key.split(".");
    let value: any = translations;
    
    for (const k of keys) {
      if (value && typeof value === "object" && k in value) {
        value = value[k];
      } else {
        warnMissingKey(key);
        return key;
      }
    }
    
    return value;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, tRaw }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
}
